import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Trophy, Lock, Sparkles, ArrowRight, Play, Star } from "lucide-react";

/**
 * Gamified "Journey to Researcher" roadmap. Loads admin-configured course nodes
 * from /api/landing/journey and renders them as a level-map: zigzag SVG path,
 * glowing checkpoint "coins", boss-level final stop, locked nodes for premium
 * courses. Length is fully dynamic — admin picks 0–N courses.
 */
export default function JourneyToResearcher() {
  const [data, setData] = useState({ title: "", subtitle: "", nodes: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/landing/journey")
      .then((r) => setData(r.data || { nodes: [] }))
      .catch(() => setData({ nodes: [] }))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && (!data.nodes || data.nodes.length === 0)) return null;

  const nodes = data.nodes || [];
  const count = nodes.length;
  const positions = nodes.map((_, i) => {
    const x = count === 1 ? 600 : 80 + i * (1040 / (count - 1));
    const y = i % 2 === 0 ? 230 : 90;
    return { x, y };
  });
  const pathD = positions.length === 0
    ? ""
    : positions.reduce((acc, p, i) => {
        if (i === 0) return `M ${p.x} ${p.y}`;
        const prev = positions[i - 1];
        const cx1 = prev.x + 100;
        const cy1 = prev.y;
        const cx2 = p.x - 100;
        const cy2 = p.y;
        return `${acc} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${p.x} ${p.y}`;
      }, "");

  const title = data.title || "Your research career — as a level map.";
  const subtitle =
    data.subtitle ||
    "Each checkpoint is a real KhojByte course. Beat the levels in order, or jump in at your stage. Boss level publishes you in a Q1 journal.";

  return (
    <section
      className="relative kb-game-board border-y border-kb-divider overflow-hidden"
      data-testid="journey-section"
    >
      <div aria-hidden className="absolute inset-0 kb-kinetic-hex opacity-30" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
          <div className="lg:col-span-7">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-purple mb-3 inline-flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> JOURNEY · LEVEL MAP
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-kb-ink leading-[1.08]">{title}</h2>
          </div>
          <p className="lg:col-span-5 text-kb-ink-muted">{subtitle}</p>
        </div>

        {/* DESKTOP / TABLET — zigzag map */}
        <div className="hidden md:block relative" style={{ minHeight: 360 }}>
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 1200 320"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="kbgame-path" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#7A44FC" />
                <stop offset="60%" stopColor="#A47BFF" />
                <stop offset="100%" stopColor="#FFC857" />
              </linearGradient>
              <path id="kbgame-path-route" d={pathD} />
            </defs>
            <path d={pathD} className="kb-game-path-bg" fill="none" strokeWidth="14" strokeLinecap="round" />
            <path d={pathD} className="kb-game-path-fg" fill="none" strokeWidth="4" strokeLinecap="round" />
            <circle r="6" className="kb-game-spark">
              <animateMotion dur="9s" repeatCount="indefinite">
                <mpath href="#kbgame-path-route" />
              </animateMotion>
            </circle>
          </svg>

          <div className="relative" style={{ height: 360 }}>
            {nodes.map((n, i) => {
              const isBoss = i === count - 1;
              const isLocked = !!n.is_premium;
              const p = positions[i];
              const leftPct = (p.x / 1200) * 100;
              const topPct = (p.y / 320) * 100;
              return (
                <Link
                  key={n.id}
                  to={`/dashboard/courses/${n.id}`}
                  data-testid={`journey-node-${i}`}
                  className={`absolute kb-game-node ${isBoss ? "is-boss" : ""} ${isLocked ? "is-locked" : ""}`}
                  style={{ left: `${leftPct}%`, top: `${topPct}%`, transform: "translate(-50%, -50%)", width: 180 }}
                >
                  <div className="flex flex-col items-center">
                    <div
                      className="kb-game-coin kb-game-bob w-16 h-16 rounded-full flex items-center justify-center text-white font-serif text-lg bg-kb-purple relative"
                      style={{ animationDelay: `${i * 0.35}s` }}
                    >
                      {isBoss ? (
                        <Trophy className="w-7 h-7" strokeWidth={1.8} />
                      ) : isLocked ? (
                        <Lock className="w-6 h-6" strokeWidth={1.8} />
                      ) : (
                        <span className="kb-mono text-sm tracking-wider">{String(i + 1).padStart(2, "0")}</span>
                      )}
                      {isBoss && (
                        <Star
                          className="w-3.5 h-3.5 absolute -top-1.5 -right-1.5 text-amber-300 fill-amber-300"
                          strokeWidth={1.5}
                        />
                      )}
                    </div>
                    <div className="mt-3 kb-card px-3.5 py-2.5 bg-white text-center w-full min-w-[160px] shadow-sm">
                      <div className="kb-mono text-[9px] uppercase tracking-[0.18em] text-kb-ink-light">
                        {isBoss ? "BOSS · " : `LVL ${String(i + 1).padStart(2, "0")} · `}
                        {n.level || "ALL LEVELS"}
                      </div>
                      <div className="font-serif text-sm text-kb-ink mt-1 leading-tight line-clamp-2">{n.title}</div>
                      <div className="mt-1.5 kb-mono text-[9px] uppercase tracking-wider text-kb-purple inline-flex items-center gap-1">
                        {isLocked ? "UNLOCK" : "PLAY"} <Play className="w-2.5 h-2.5 fill-current" />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* MOBILE — vertical board */}
        <ol className="md:hidden space-y-3 relative">
          <div
            aria-hidden
            className="absolute left-7 top-3 bottom-3 w-[2px] bg-gradient-to-b from-kb-purple/30 via-kb-purple/60 to-amber-400/60 rounded-full"
          />
          {nodes.map((n, i) => {
            const isBoss = i === count - 1;
            const isLocked = !!n.is_premium;
            return (
              <li key={n.id} className="relative pl-16">
                <Link
                  to={`/dashboard/courses/${n.id}`}
                  className="kb-card p-3.5 flex items-center gap-3"
                  data-testid={`journey-node-mobile-${i}`}
                >
                  <div
                    className={`kb-game-coin absolute left-0 top-1 w-14 h-14 rounded-full flex items-center justify-center text-white bg-kb-purple ${
                      isBoss ? "is-boss" : ""
                    } ${isLocked ? "is-locked" : ""}`}
                  >
                    {isBoss ? (
                      <Trophy className="w-6 h-6" />
                    ) : isLocked ? (
                      <Lock className="w-5 h-5" />
                    ) : (
                      <span className="kb-mono text-sm">{String(i + 1).padStart(2, "0")}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light">
                      {isBoss ? "BOSS · " : `LVL ${String(i + 1).padStart(2, "0")} · `}
                      {n.level || "ALL LEVELS"}
                    </div>
                    <div className="font-serif text-base text-kb-ink leading-tight">{n.title}</div>
                    <div className="mt-1 kb-mono text-[10px] uppercase tracking-wider text-kb-purple inline-flex items-center gap-1">
                      {isLocked ? "UNLOCK" : "PLAY"} <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ol>

        <div className="mt-12 text-center">
          <Link to="/dashboard/courses" className="kb-btn-primary inline-flex" data-testid="journey-start-cta">
            Start playing <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
